import { useState, useMemo } from 'react';
import { X, User, Mail, Phone, Shield, CreditCard, Save, Plus, Check, AlertCircle } from 'lucide-react';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { useData } from '../../../contexts/DataContext';

const ROLES = [
  { value: 'tech', label: 'Technician', description: 'Access to basic features, own cards only' },
  { value: 'pm', label: 'Project Manager', description: 'Can view all charges, approve receipts' },
  { value: 'admin', label: 'Administrator', description: 'Full access to admin panel and settings' },
  { value: 'demo', label: 'Demo User', description: 'Read-only access, cannot modify any data' },
];

const STATUSES = [
  { value: 'active', label: 'Active', color: colors.green },
  { value: 'inactive', label: 'Inactive', color: colors.red },
  { value: 'invited', label: 'Invited', color: colors.orange },
];

export default function UserEditor({ user, isNew, onClose }) {
  const { createUser, updateUser, assignCardsToUser, getAllCards, users } = useData();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [cardSearch, setCardSearch] = useState('');

  const [formData, setFormData] = useState({
    email: user?.email || '',
    displayName: user?.displayName || '',
    phone: user?.phone || '',
    role: user?.role || 'tech',
    status: user?.status || 'active',
    assignedCards: user?.assignedCards || [],
  });

  // Get all cards and filter available ones
  const allCards = useMemo(() => getAllCards(), [getAllCards]);

  // Get cards that are available (not assigned to other users)
  const availableCards = useMemo(() => {
    const assignedToOthers = new Set();
    users.forEach(u => {
      if (u.uid !== user?.uid) {
        (u.assignedCards || []).forEach(c => assignedToOthers.add(c));
      }
    });

    let available = allCards.filter(c =>
      !assignedToOthers.has(c.cardLast4) &&
      c.status === 'active'
    );

    if (cardSearch) {
      const term = cardSearch.toLowerCase();
      available = available.filter(c =>
        c.cardLast4.includes(term) ||
        (c.holderName || '').toLowerCase().includes(term) ||
        (c.cardName || '').toLowerCase().includes(term)
      );
    }

    return available;
  }, [allCards, users, user, cardSearch]);

  // Get currently assigned cards with details
  const assignedCardDetails = useMemo(() => {
    return formData.assignedCards.map(cardLast4 => {
      const card = allCards.find(c => c.cardLast4 === cardLast4);
      return card || { cardLast4, holderName: 'Unknown', status: 'unknown' };
    });
  }, [formData.assignedCards, allCards]);

  const handleSave = async () => {
    setError(null);

    // Validation
    if (!formData.email) {
      setError('Email is required');
      return;
    }

    if (!formData.email.includes('@')) {
      setError('Invalid email address');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await createUser({
          email: formData.email,
          displayName: formData.displayName,
          phone: formData.phone || null,
          role: formData.role,
          status: formData.status,
          assignedCards: formData.assignedCards,
        });
      } else {
        await updateUser(user.uid, {
          displayName: formData.displayName,
          phone: formData.phone || null,
          role: formData.role,
          status: formData.status,
        });

        // Update card assignments separately
        if (JSON.stringify(user.assignedCards) !== JSON.stringify(formData.assignedCards)) {
          await assignCardsToUser(user.uid, formData.assignedCards);
        }
      }

      onClose();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddCard = (cardLast4) => {
    if (!formData.assignedCards.includes(cardLast4)) {
      setFormData(prev => ({
        ...prev,
        assignedCards: [...prev.assignedCards, cardLast4],
      }));
    }
  };

  const handleRemoveCard = (cardLast4) => {
    setFormData(prev => ({
      ...prev,
      assignedCards: prev.assignedCards.filter(c => c !== cardLast4),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-2xl"
        style={{ background: colors.cardBg }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: colors.border }}>
          <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
            {isNew ? 'Add New User' : 'Edit User'}
          </h2>
          <button onClick={onClose} style={{ color: colors.textMuted }}>
            <X size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 rounded-lg flex items-center gap-2" style={{ background: colors.redDim }}>
            <AlertCircle size={18} style={{ color: colors.red }} />
            <span style={{ color: colors.red }}>{error}</span>
          </div>
        )}

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Email Address
            </label>
            <div className="relative">
              <Mail
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: colors.textMuted }}
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                disabled={!isNew}
                className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
                style={{
                  background: isNew ? colors.inputBg : colors.black,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                  opacity: isNew ? 1 : 0.6,
                }}
                placeholder="user@example.com"
              />
            </div>
            {!isNew && (
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                Email cannot be changed after creation
              </p>
            )}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Display Name
            </label>
            <div className="relative">
              <User
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: colors.textMuted }}
              />
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
                style={{
                  background: colors.inputBg,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                }}
                placeholder="John Smith"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Phone Number (optional)
            </label>
            <div className="relative">
              <Phone
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: colors.textMuted }}
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
                style={{
                  background: colors.inputBg,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                }}
                placeholder="(414) 555-1234"
              />
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              <Shield size={14} className="inline mr-1" />
              Role
            </label>
            <div className="space-y-2">
              {ROLES.map(role => (
                <label
                  key={role.value}
                  className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
                  style={{
                    background: formData.role === role.value ? colors.yellowDim : colors.inputBg,
                    border: `1px solid ${formData.role === role.value ? colors.yellow : colors.border}`,
                  }}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={formData.role === role.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium" style={{ color: colors.textPrimary }}>{role.label}</p>
                    <p className="text-sm" style={{ color: colors.textMuted }}>{role.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Status
            </label>
            <div className="flex gap-2">
              {STATUSES.map(status => (
                <button
                  key={status.value}
                  onClick={() => setFormData(prev => ({ ...prev, status: status.value }))}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: formData.status === status.value ? status.color + '20' : colors.inputBg,
                    color: formData.status === status.value ? status.color : colors.textMuted,
                    border: `1px solid ${formData.status === status.value ? status.color : colors.border}`,
                  }}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Card Assignments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                <CreditCard size={14} className="inline mr-1" />
                Assigned Cards
              </label>
              <button
                onClick={() => setShowCardPicker(!showCardPicker)}
                className="text-sm px-2 py-1 rounded-lg flex items-center gap-1"
                style={{ background: colors.yellowDim, color: colors.yellow }}
              >
                <Plus size={14} />
                Add Card
              </button>
            </div>

            {/* Assigned Cards List */}
            {assignedCardDetails.length > 0 ? (
              <div className="space-y-2 mb-3">
                {assignedCardDetails.map(card => (
                  <div
                    key={card.cardLast4}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ background: colors.inputBg }}
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard size={16} style={{ color: colors.textMuted }} />
                      <span className="font-mono" style={{ color: colors.textPrimary }}>
                        ****{card.cardLast4}
                      </span>
                      <span className="text-sm" style={{ color: colors.textMuted }}>
                        {card.holderName || card.cardName}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveCard(card.cardLast4)}
                      className="p-1 rounded"
                      style={{ color: colors.red }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm mb-3" style={{ color: colors.textMuted }}>
                No cards assigned. Cards will be matched by name as fallback.
              </p>
            )}

            {/* Card Picker */}
            {showCardPicker && (
              <div className="rounded-lg p-3" style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
                <input
                  type="text"
                  value={cardSearch}
                  onChange={(e) => setCardSearch(e.target.value)}
                  placeholder="Search cards..."
                  className="w-full px-3 py-2 rounded-lg mb-2 outline-none text-sm"
                  style={{
                    background: colors.cardBg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`,
                  }}
                />
                <div className="max-h-40 overflow-auto space-y-1">
                  {availableCards.length === 0 ? (
                    <p className="text-sm text-center py-2" style={{ color: colors.textMuted }}>
                      No available cards
                    </p>
                  ) : (
                    availableCards.map(card => (
                      <div
                        key={card.cardLast4}
                        className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:opacity-80"
                        style={{ background: colors.cardBg }}
                        onClick={() => handleAddCard(card.cardLast4)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm" style={{ color: colors.textPrimary }}>
                            ****{card.cardLast4}
                          </span>
                          <span className="text-xs" style={{ color: colors.textMuted }}>
                            {card.holderName || card.cardName}
                          </span>
                        </div>
                        <Plus size={16} style={{ color: colors.green }} />
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t" style={{ borderColor: colors.border }}>
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1">
            {saving ? <LoadingSpinner size="sm" color={colors.black} /> : <Save size={18} />}
            {isNew ? 'Create User' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
